import { toFullName } from '@repo/toolkit/to-full-name'
import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import { useQuery } from '@tanstack/react-query'

import NumberInput from '../../../../../../packages/ui/components/atoms/input/number-input'
import { getCenters } from '../../../api/centers/get-centers'
import { getCountries } from '../../../api/countries/get-countries'
import { getEnrollments } from '../../../api/enrollments/get-enrollments'
import { getPeriods } from '../../../api/periods/get-periods'
import { getUsers } from '../../../api/user/get-users'
import {
  EventStatusEnum,
  eventStatusEnumMapping
} from '../../../lib/types/enums/center-event/event-status.enum'
import {
  EventTypeEnum,
  eventTypeEnumMapping
} from '../../../lib/types/enums/center-event/event-type.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { CenterEventFilter } from '../../../lib/types/models/center-event/center-event'
import { CenterEventProperties } from '../../../lib/types/models/center-event/properties.center-event'
import CountryFlag from '../../elements/country-flag'

interface Props {
  centerId?: number
  filter?: CenterEventFilter
  onFilterChange: (filter: CenterEventFilter) => void
}

export function CenterEventsFilter({ centerId, filter, onFilterChange }: Props) {
  function setFilter<K extends keyof CenterEventFilter>(key: K, value: CenterEventFilter[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: centers } = useQuery({
    queryKey: ['centers'],
    queryFn: () => getCenters(),
    enabled: !centerId
  })

  const { data: enrollments } = useQuery({
    queryKey: ['enrollments', { centerId }],
    queryFn: () =>
      getEnrollments({ filter: { centerIds: centerId ? [centerId] : undefined } }, [
        'id',
        'childFirstName',
        'childLastName'
      ]),
    enabled: !!centerId
  })

  const { data: users } = useQuery({
    queryKey: ['users', 'admins'],
    queryFn: () => getUsers({ filter: { roles: [RoleEnum.ADMIN] } })
  })

  if (!filter) return null

  return (
    <div className={'flex flex-col gap-3 mt-2'}>
      <MultiSelectInput
        variant="primary"
        options={Object.values(EventStatusEnum).map((status) => ({
          label: eventStatusEnumMapping[status].label,
          value: status
        }))}
        selectedOptions={filter?.statuses?.map((status) => ({
          label: eventStatusEnumMapping[status].label,
          value: status
        }))}
        onChange={(statuses) => {
          onFilterChange({ ...filter, statuses })
        }}
        label="Statut de l'évènement"
      />
      <MultiSelectInput
        selectedOptions={filter?.types?.map((type) => ({
          label: eventTypeEnumMapping[type].label,
          value: type
        }))}
        variant={'primary'}
        onChange={(types) => {
          onFilterChange({ ...filter, types })
        }}
        options={Object.values(EventTypeEnum).map((type) => ({
          label: eventTypeEnumMapping[type].label,
          value: type
        }))}
        label="Type d'évènement"
      />
      <MultiSelectInput
        selectedOptions={filter?.studentIds?.map((studentId) => ({
          label: CenterEventProperties.searchStudentName(studentId, enrollments?.data ?? []),
          value: studentId
        }))}
        variant={'primary'}
        onChange={(studentIds) => {
          onFilterChange({ ...filter, studentIds })
        }}
        disabled={enrollments?.data.length === 0}
        options={enrollments?.data.map((enrollment) => ({
          label: CenterEventProperties.getStudentName(enrollment),
          value: enrollment.id
        }))}
        label="NOM du jeune"
      />
      {!centerId && (
        <MultiSelectInput
          selectedOptions={filter?.centerIds?.map((centerId) => ({
            label: centers?.data.find((center) => center.id === centerId)?.name ?? '',
            value: centerId
          }))}
          variant={'primary'}
          onChange={(centerIds) => {
            onFilterChange({ ...filter, centerIds })
          }}
          options={centers?.data.map((center) => ({
            label: center.name,
            value: center.id
          }))}
          label="Centre"
        />
      )}
      <div className="flex gap-4">
        <DateStringInput
          mode="over"
          label="Date de début "
          value={filter?.startDate}
          onChange={(date) => onFilterChange({ ...filter, startDate: date })}
        />

        <DateStringInput
          mode="over"
          label="Date de fin"
          value={filter?.endDate}
          onChange={(date) => onFilterChange({ ...filter, endDate: date })}
        />
      </div>
      <DateStringInput
        mode="over"
        label="Evènement créé le"
        value={filter?.createdAt}
        onChange={(date) => onFilterChange({ ...filter, createdAt: date })}
      />

      <DateStringInput
        label="Dernière activité"
        value={filter?.lastActivity}
        mode="over"
        onChange={(date) => onFilterChange({ ...filter, lastActivity: date })}
      />
      <NumberInput
        label={"Durée d'inactivité (en jours)"}
        value={filter.inactiveDuration}
        onChange={(e) => setFilter('inactiveDuration', +e.target.value)}
      />
      <MultiSelectInput
        selectedOptions={filter?.assignedToIds?.map((createdById) => ({
          label: CenterEventProperties.searchUserName(createdById, users?.data ?? []),
          value: createdById
        }))}
        variant={'primary'}
        onChange={(assignedToIds) => {
          onFilterChange({ ...filter, assignedToIds })
        }}
        options={users?.data.map((user) => ({
          label: toFullName(user.firstName, user.lastName),
          value: user.id
        }))}
        label="Suivi par"
      />
      <MultiSelectInput
        searchable
        variant={'primary'}
        onChange={(countries) => onFilterChange({ ...filter, countryIds: countries })}
        options={countries?.data.map(({ id, name }) => ({
          label: name,
          value: id,
          custom: <CountryFlag possibleCountry={name} />
        }))}
        selectedOptions={filter?.countryIds?.map((countryId) => ({
          label: countries?.data.find((c) => c.id === countryId)?.name ?? '',
          value: countryId
        }))}
        label="Pays"
      />

      <MultiSelectInput
        searchable
        selectedOptions={filter?.periodIds?.map((periodId) => ({
          label: periods?.data.find((period) => period.id === periodId)?.name ?? '',
          value: periodId
        }))}
        variant={'primary'}
        onChange={(periodIds) => {
          onFilterChange({ ...filter, periodIds })
        }}
        options={periods?.data.map((period) => ({
          label: period.name,
          value: period.id
        }))}
        label="Période"
      />
    </div>
  )
}
